import PropTypes from 'prop-types';

import {Page as BasePage} from '@amblique/contentful/app/components/contentful/page';
import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';
import {CategoryLandingRegion} from '@amblique/king-living/app/components/contentful/category-landing-region';
import {PAGE_BLOG_METADATA_TAG_IDS} from '@amblique/king-living/app/contentful/utils/blog-metadata-tag-ids';
import {contentfulPageTypeBodySuffix} from '@amblique/king-living/app/contentful/utils/page-type-body-slug';
import {useBodyPageType} from '@amblique/king-living/app/hooks/use-body-page-type';

/**
 * CMS `pageType` values that should render their region modules via the
 * per-module "fake lazy reveal" (`<CategoryLandingRegion>`). Today only
 * Category Landing Pages opt in; the list lives here so a future content
 * type can join the same UX without changing this component.
 */
const LAZY_REVEAL_PAGE_TYPES = new Set(['category']);

const isLazyRevealPage = (page) => {
  const raw = page?.pageType;

  if (typeof raw !== 'string' || raw === '') return false;

  return LAZY_REVEAL_PAGE_TYPES.has(raw.trim().toLowerCase());
};

const renderDefaultRegionItems = (items) => items.map((component) => (
  <ContentfulComponent key={component.sys.id} component={component} />
));

/**
 * Fallback body suffix when CMS `pageType` is empty.
 * Priority: productId → categoryId → storeId → blog tag → generic (home relies on CMS `homepage`).
 *
 * @param {Contentful.Page} page
 * @returns {string}
 */
const getPageType = (page) => {
  if (page?.productId) return 'product-page';
  if (page?.categoryId) return 'category-listing-page';
  if (page?.storeId) return 'store-detail-page';

  const tagIds = page?.contentfulMetadata?.tags?.map((t) => t?.id) ?? [];

  if (PAGE_BLOG_METADATA_TAG_IDS.some((id) => tagIds.includes(id))) return 'blog-page';

  return 'generic-page';
};

/**
 * Body suffix for `page--*`: CMS `pageType` (validated enum → slug, see {@link contentfulPageTypeBodySuffix}),
 * otherwise {@link getPageType}.
 *
 * @param {Contentful.Page} page
 * @returns {string}
 */
const resolveBodyPageType = (page) =>
  contentfulPageTypeBodySuffix(page?.pageType) ?? getPageType(page);

/**
 * @override
 * Applies a page-type class to document.body while the page is mounted,
 * enabling page-specific CSS targeting (e.g. body.page--home-page .my-component).
 *
 * Classes added: `page` and `page--<suffix>` — suffix follows Contentful `pageType` mapping
 * (e.g. `homepage`→`home-page`, `blog`→`blog-page`) or hyphenated slug for ad-hoc values;
 * when `pageType` is unset: product/category/store/blog-tag/generic fallback.
 *
 * For Category Landing Pages (`page.pageType === 'category'`) the
 * region modules are rendered through `<CategoryLandingRegion>` which
 * mounts every module up front but holds modules past the initial 4
 * at `opacity: 0` until each one scrolls into the viewport, then
 * fades them in with a CSS transition. This is a UI-only "fake lazy
 * load" — all Contentful data is still fetched up-front by the page
 * query.
 */
export const Page = ({page, renderRegionItems, ...props}) => {
  useBodyPageType(resolveBodyPageType(page));

  // Respect an explicit caller-supplied renderer first (lets a future
  // surface override the reveal strategy); otherwise auto-opt-in the
  // per-module reveal for category landing pages. Everything else
  // falls through to the BasePage default (one ContentfulComponent
  // per item) by leaving `renderRegionItems` undefined.
  //
  // `pageKey` forces RevealOnScroll remount when mega-menu navigates
  // between category landings that reuse the same Contentful entry ids.
  const resolvedRenderRegionItems =
    renderRegionItems ??
    (isLazyRevealPage(page) ?
      (items) => (
        <CategoryLandingRegion
          key={page.sys?.id}
          items={items}
          pageKey={page.sys?.id}
        />
      ) :
      renderDefaultRegionItems);

  return (
    <BasePage page={page} renderRegionItems={resolvedRenderRegionItems} {...props} />
  );
};

Page.displayName = 'Page';
Page.propTypes = {
  page: PropTypes.object.isRequired,

  /**
   * Optional override for region rendering — forwarded to `BasePage`.
   * When omitted, category landing pages auto-opt-in to the per-module
   * reveal via `<CategoryLandingRegion>`; all other page types use the
   * base default (one `<ContentfulComponent>` per item).
   */
  renderRegionItems: PropTypes.func,
};
